/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {navigate, ResponseData, showDialog, showSnackbar, validateEmail} from '@dvrd/dvr-controls';
import {AppContext} from "../../misc/appContext";
import User, {UserFields} from "../../../models/userModel";
import Account, {AccountFields} from "../../../models/accountModel";
import {CompanyErrors, PersonalErrors} from "./userErrorsInterface";
import {sendGaEvent} from "../../../util/analytics";
import {createErrorMessage, userHasSubscription} from "../../../util/utils";
import {onAddAccount, onSetCrumbs} from "../../../util/events";
import CommonSettings from "./commonSettings";
import {ActivateHandle, CancelType} from '../../../util/interfaces';
import CancelSubscriptionController from "../subscription/cancelSubscription/cancelSubscriptionController";
import PageChangePermission from "../../misc/pageChangePermission";
import {PermissionCallback} from "../../../util/pageChanger";
import {setJWT} from "../../../util/jwtUtil";

const USER_ERRORS: PersonalErrors = {
    address: null,
    city: null,
    email: null,
    firstname: null,
    lastname: null,
    phone: null,
    zipCode: null
};
const ACCOUNT_ERRORS: CompanyErrors = {
    address: null,
    city: null,
    companyName: null,
    email: null,
    phone: null,
    zipCode: null,
};

export default function CommonSettingsController() {
    const context = useContext(AppContext);
    const [user, setUser] = useState(context.userContext.user?.clone() ?? new User());
    const [account, setAccount] = useState(context.accountContext.account?.clone() ?? new Account());
    const [loading, setLoading] = useState(false);
    const [userErrors, setUserErrors] = useState(USER_ERRORS);
    const [accountErrors, setAccountErrors] = useState(ACCOUNT_ERRORS);
    const [countryCode, setCountryCode] = useState('BE');
    const [accountChanged, setAccountChanged] = useState(false);
    const userChanged = useRef(false);
    const hasMultipleAccounts = context.accountContext.accounts.length > 1;
    const cancelRef = useRef<ActivateHandle>(null);

    function onChangeUser(key: keyof UserFields) {
        return function(value: string) {
            userChanged.current = true;
            const usr = user.clone();
            usr.setField(key, value);
            setUser(usr);
        }
    }

    function onChangeAccount(key: keyof AccountFields) {
        return function(value: string) {
            setAccountChanged(true);
            const acc = account.clone();
            acc.setField(key, value);
            setAccount(acc);
        }
    }

    function onChangeCountryCode(value: string) {
        setCountryCode(value);
    }

    function onChangeCountryVat(countryCode: string) {
        return function(value: string) {
            setAccountChanged(true);
            const acc = account.clone();
            const {vatNumbers} = acc;
            if (value) vatNumbers[countryCode] = value;
            else delete vatNumbers[countryCode];
            setAccount(acc);
        }
    }

    function onResetUserError(key: keyof PersonalErrors) {
        return function() {
            setUserErrors(Object.assign({}, userErrors, {[key]: null}));
        }
    }

    function onResetAccountError(key: keyof CompanyErrors) {
        return function() {
            setAccountErrors(Object.assign({}, accountErrors, {[key]: null}));
        }
    }

    function onSaveBeforePageChange(id: number | null, callback: PermissionCallback, evt: React.MouseEvent) {
        if (userChanged.current)
            onUpdateUser(evt, () => {
                if (accountChanged)
                    onUpdateAccount(evt, () => {
                        callback(id, true);
                    });
                else callback(id, true);
            });
        else if (accountChanged)
            onUpdateAccount(evt, () => {
                callback(id, true);
            });
        else callback(id, true);
    }

    function onUpdateUser(_: React.MouseEvent | null, callback?: VoidFunction) {
        if (!validateUser()) return;
        setLoading(true);
        user.update(undefined, (user: User, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                userChanged.current = false;
                context.onChangeContext({user}, () => {
                    showSnackbar('De wijzigingen zijn opgeslagen')
                    if (callback) callback();
                })
            } else showDialog(
                createErrorMessage(data.message ?? 'Het wijzigen van je persoonlijke gegevens is niet gelukt.'),
                'Wijzigen mislukt');
        });
    }

    function onUpdateAccount(_: React.MouseEvent | null, callback?: VoidFunction) {
        if (!validateAccount()) return;
        setLoading(true);
        account.update(undefined, undefined, undefined, (account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setAccountChanged(false);
                const accounts = context.accountContext.accounts;
                for (let i = 0; i < accounts.length; i++) {
                    if (accounts[i].id === account.id) {
                        accounts.splice(i, 1, account);
                        break;
                    }
                }
                context.onChangeContext({account, accounts}, () => {
                    showSnackbar('De wijzigingen zijn opgeslagen');
                    if (callback) callback();
                });
            } else showDialog(
                createErrorMessage(data.message ?? 'Het wijzigen van je bedrijfsgegevens is niet gelukt.'),
                'Wijzigen mislukt');
        });
    }

    function onClickDeleteAccount() {
        sendGaEvent('Click', 'Algemene instellingen', 'Verkoopaccount verwijderen');
        showDialog('Weet je zeker dat je je account wilt verwijderen? Hierbij gaan alle gegevens definitief ' +
            'verloren.', 'Account verwijderen', [{label: 'Nee', onClick: onCancelDelete},
            {label: 'Ja', onClick: onConfirmDeleteAccount, primary: false}]);
    }

    function onConfirmDeleteAccount() {
        sendGaEvent('Click', 'Algemene instellingen', 'Bevestig verkoopaccount verwijderen');
        setLoading(true);
        account.delete((account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Je account is verwijderd');
                const {is_last} = data;
                if (is_last) onLastAccountDeleted();
                else {
                    const accounts = context.accountContext.accounts.filter(
                        (acc: Account) => acc.id !== account.id);
                    if (accounts.length) {
                        context.onChangeContext({accounts: accounts, account: accounts[0]}, () => {
                            navigate('/facturen');
                        })
                    } else location.reload();
                }
            } else showDialog(createErrorMessage(data.message ?? 'Het verwijderen van je account is niet gelukt.'),
                'Verwijderen mislukt');
        });
    }

    function onCancelDelete() {
        sendGaEvent('Click', 'Algemene instellingen', 'Annuleer account verwijderen');
    }

    function onAskCancelReason() {
        const {user} = context.userContext;
        if (!userHasSubscription(user) || !user?.isSubscriptionOwner() || !cancelRef.current)
            // User has already ended the subscription before, or the ref is not available
            showDialog('Weet je zeker dat je je gehele account wilt verwijderen?', 'Geheel Bolmate account verwijderen',
                ['Nee', {label: 'Ja', onClick: onDeleteUser, primary: false}]);
        else cancelRef.current.activate();
    }

    function onLastAccountDeleted() {
        showDialog('Je hebt geen accounts meer op het platform. Wil je een nieuw account aanmaken?',
            'Account verwijderd', [{label: 'Login verwijderen', onClick: onAskCancelReason, primary: false},
                {label: 'Account aanmaken', onClick: _onAddAccount}])
    }

    function _onAddAccount() {
        sendGaEvent('Click', 'Algemene instellingen', 'Nieuw account maken');
        context.onChangeContext({accounts: [], account: null}, onAddAccount);
    }

    function onDeleteUser() {
        sendGaEvent('Click', 'Algemene instellingen', 'Gebruiker verwijderen');
        setLoading(true);
        user.delete((success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar('Je login is verwijderd');
                setJWT(null);
                context.onChangeContext({user: null, account: null, accounts: []}, () => {
                    navigate('/inloggen');
                });
            } else showDialog(createErrorMessage(data.message ?? 'Het verwijderen van je login is niet gelukt.'),
                'Verwijderen mislukt')
        });
    }

    function validateUser(): boolean {
        const errors = Object.assign({}, userErrors);
        let valid: boolean = true;
        if (!user.firstname) {
            valid = false;
            errors.firstname = 'Vul je voornaam in';
        }
        if (!user.lastname) {
            valid = false;
            errors.lastname = 'Vul je achternaam in';
        }
        if (!user.telNumber) {
            valid = false;
            errors.phone = 'Vul het telefoonnummer in';
        }
        if (!user.zipCode) {
            valid = false;
            errors.zipCode = 'Vul de postcode in';
        }
        if (!user.email) {
            valid = false;
            errors.email = 'Vul je e-mailadres in';
        } else if (!validateEmail(user.email)) {
            valid = false;
            errors.email = 'Vul een geldig e-mailadres in';
        }
        if (!user.address) {
            valid = false;
            errors.address = 'Vul het adres in';
        }
        if (!user.city) {
            valid = false;
            errors.city = 'Vul de stad in';
        }
        if (!valid) setUserErrors(errors);
        return valid;
    }

    function validateAccount(): boolean {
        const errors = Object.assign({}, accountErrors);
        let valid: boolean = true;
        if (!account.companyName) {
            valid = false;
            errors.companyName = 'Vul de bedrijfsnaam in';
        }
        if (!account.telNumber) {
            valid = false;
            errors.phone = 'Vul het telefoonnummer in';
        }
        if (!account.zipCode) {
            valid = false;
            errors.zipCode = 'Vul de postcode in';
        }
        if (!account.email) {
            valid = false;
            errors.email = 'Vul het e-mailadres in';
        } else if (!validateEmail(account.email)) {
            valid = false;
            errors.email = 'Vul een geldig e-mailadres in';
        }
        if (!account.address) {
            valid = false;
            errors.address = 'Vul het adres in';
        }
        if (!account.city) {
            valid = false;
            errors.city = 'Vul de stad in';
        }
        if (!valid) setAccountErrors(errors);
        return valid;
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'Algemeen'}]);
    }, []);

    useEffect(() => {
        const {account} = context.accountContext;
        if (account)
            setAccount(account.clone());
    }, [context.accountContext.account]);

    return (
        <>
            <CommonSettings onChangeUser={onChangeUser} onChangeAccount={onChangeAccount} onUpdateUser={onUpdateUser}
                            onUpdateAccount={onUpdateAccount} onClickDelete={onClickDeleteAccount}
                            onResetPersonalErrors={onResetUserError} onResetCompanyErrors={onResetAccountError}
                            onChangeCountryCode={onChangeCountryCode} onChangeCountryVat={onChangeCountryVat}
                            onClickDeleteUser={onAskCancelReason} user={user} account={account}
                            personalErrors={userErrors} companyErrors={accountErrors} loading={loading}
                            countryCode={countryCode} hasMultipleAccounts={hasMultipleAccounts}
                            accountChanged={accountChanged}/>
            <CancelSubscriptionController ref={cancelRef} onSuccess={onDeleteUser} type={CancelType.ACCOUNT}/>
            <PageChangePermission changed={userChanged.current || accountChanged} onDeny={onSaveBeforePageChange}
                                  denyText='Ja, wijzigingen opslaan' allowText='Nee, pagina verlaten'
                                  allowSecondary={true}>
                De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?
            </PageChangePermission>
        </>
    );
}